/**
 * Unlock Settings Page
 * V1 Implementation - Verified Demand
 * Configure the Unlock Offer/Price feature
 */
import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import { Lock, Save, Plus, Trash2, Star, ChevronDown } from "lucide-react";
import DealerBrandingTab from "../components/admin/DealerBrandingTab";

// Top Headline Text options (6 predefined options)
const SCRIPT_TEXT_OPTIONS = [
  { key: "", label: "(Use template default)" },
  { key: "WANT_A_LOWER_PRICE", label: "Want a Lower Price?" },
  { key: "GET_OUR_BEST_PRICE", label: "Get Our Best Price!" },
  { key: "UNLOCK_YOUR_BEST_PRICE", label: "Unlock Your Best Price" },
  { key: "SEE_TODAYS_PRICE", label: "See Today's Price" },
  { key: "CLICK_TO_REVEAL_PRICE", label: "Click to Reveal Price" },
  { key: "START_HERE", label: "Start Here" },
];

const UnlockSettingsPage = ({ onNavigate } = {}) => {
  const { apiFetch, user } = useAuth();
  const toast = useToast();
  const [settings, setSettings] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  // Flexible Pricing state
  const [pricingRules, setPricingRules] = useState([]);
  const [newRule, setNewRule] = useState({ rule_type: "condition", condition: "new", vin: "", unlock_price: "" });
  const [isAddingRule, setIsAddingRule] = useState(false);

  // Smart Pricing Rules state (year/make/model/trim → stored as vin_override rules).
  // iter93 — Issue 10: Add/Delete moved to dedicated Pricing Rules pages.
  // Kept the smartRules list for the read-only summary table.
  const [smartRules, setSmartRules] = useState([]);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await apiFetch("/unlock/settings", { method: "GET" });
        setSettings(res.data);
        // Also fetch pricing rules
        try {
          const rulesRes = await apiFetch("/unlock/pricing-rules", { method: "GET" });
          const allRules = rulesRes.data?.rules || [];
          // Separate smart rules (vin_override rules that look like "YEAR MAKE MODEL...")
          // from standard flat rules
          const basicRules = allRules.filter(r => r.rule_type !== "vin_override" || !isSmartRuleIdentifier(r.vin));
          const smartRuleList = allRules.filter(r => r.rule_type === "vin_override" && isSmartRuleIdentifier(r.vin));
          setPricingRules(basicRules);
          setSmartRules(smartRuleList);
        } catch (e) { /* pricing rules are optional */ }
      } catch (e) {
        toast.error("Failed to load unlock settings");
      } finally {
        setIsLoading(false);
      }
    };
    fetchSettings();
  }, [apiFetch, toast]);

  // Smart rule identifiers use "YEAR MAKE MODEL [TRIM]" format with a VD_SMART prefix
  const SMART_PREFIX = "VD_SMART:";
  const isSmartRuleIdentifier = (vin) => vin && vin.startsWith(SMART_PREFIX);
  const parseSmartIdentifier = (vin) => {
    if (!isSmartRuleIdentifier(vin)) return null;
    const parts = vin.replace(SMART_PREFIX, "").split("|");
    return { year: parts[0] || "", make: parts[1] || "", model: parts[2] || "", trim: parts[3] || "" };
  };

  const handleAddRule = async () => {
    if (!newRule.unlock_price) {
      toast.error("Unlock price is required");
      return;
    }
    if (newRule.rule_type === "vin_override" && !newRule.vin) {
      toast.error("VIN is required for VIN-specific overrides");
      return;
    }
    setIsAddingRule(true);
    try {
      const body = {
        rule_type: newRule.rule_type,
        unlock_price: newRule.unlock_price,
        ...(newRule.rule_type === "condition" ? { condition: newRule.condition } : {}),
        ...(newRule.rule_type === "vin_override" ? { vin: newRule.vin } : {}),
      };
      const res = await apiFetch("/unlock/pricing-rules", { method: "POST", data: body });
      setPricingRules(prev => [res.data.rule, ...prev]);
      setNewRule({ rule_type: "condition", condition: "new", vin: "", unlock_price: "" });
      toast.success("Pricing rule added!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add rule");
    } finally {
      setIsAddingRule(false);
    }
  };

  const handleDeleteRule = async (ruleId) => {
    try {
      await apiFetch(`/unlock/pricing-rules/${ruleId}`, { method: "DELETE" });
      setPricingRules(prev => prev.filter(r => r.id !== ruleId));
      toast.success("Rule deleted");
    } catch (e) {
      toast.error("Failed to delete rule");
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Normalize unlock_notify_emails: textarea string → array of trimmed emails
      const payload = { ...settings };
      if (typeof payload.unlock_notify_emails === "string") {
        payload.unlock_notify_emails = payload.unlock_notify_emails
          .split(/[\s,;\n]+/)
          .map(e => e.trim())
          .filter(Boolean);
      }
      await apiFetch("/unlock/settings", {
        method: "PUT",
        data: payload,
      });
      toast.success("Unlock settings saved!");
    } catch (e) {
      toast.error("Failed to save settings");
    } finally {
      setIsSaving(false);
    }
  };
  
  // Get display text for selected headline
  const getHeadlineDisplayText = () => {
    if (settings?.cta_script_text_key) {
      const option = SCRIPT_TEXT_OPTIONS.find(o => o.key === settings.cta_script_text_key);
      if (option) return option.label;
    }
    return "Want a Lower Price?";  // Default headline
  };

  if (isLoading) return <div className="loading">Loading...</div>;
  if (!settings) return <div className="error">Failed to load settings</div>;

  return (
    <div className="page" data-testid="unlock-settings-page">
      <div className="page-header">
        <div className="page-header-left">
          <h2 className="page-title">Unlock Price</h2>
          <p className="page-subtitle">Configure the price unlock CTA shown on your inventory pages</p>
        </div>
        <button className="btn-primary" onClick={handleSave} disabled={isSaving} data-testid="save-unlock-settings">
          <Save size={14} /> {isSaving ? "Saving..." : "Save Changes"}
        </button>
      </div>

      <div className="unlock-settings-layout">
        <div className="settings-grid" style={{flex:'1 1 0', minWidth:0}}>
        {/* Enable/Disable Section */}
        <div className="settings-section">
          <h3>Feature Status</h3>
          <div className="setting-row">
            <label className="toggle-label">
              <span>Enable Unlock Feature</span>
              <input
                type="checkbox"
                checked={settings.enabled}
                onChange={(e) => handleChange("enabled", e.target.checked)}
              />
              <span className="toggle-switch"></span>
            </label>
            <p className="setting-description">
              When enabled, the Unlock Price button will appear on your inventory pages.
            </p>
          </div>
        </div>

        {/* iter80 — Effective Offer panel (read-only) */}
        <div className="settings-section" data-testid="effective-offer-section">
          <h3>Effective Offer</h3>
          <p className="setting-description" style={{ marginBottom: "12px" }}>
            The default unlock offer is auto-created when the feature is enabled and at least one
            pricing rule exists. The embed associates every lead and event with this offer id.
          </p>
          <div
            data-testid="effective-offer-card"
            style={{
              display: "grid",
              gridTemplateColumns: "200px 1fr",
              gap: "10px 18px",
              padding: "14px 16px",
              background: settings.enabled ? "#f0fdf4" : "#fef2f2",
              border: `1px solid ${settings.enabled ? "#bbf7d0" : "#fecaca"}`,
              borderRadius: "8px",
              fontSize: "14px",
            }}
          >
            <div style={{ color: "#475569", fontWeight: 600 }}>Status</div>
            <div data-testid="effective-offer-status">
              {settings.enabled ? (
                <span style={{ color: "#15803d", fontWeight: 600 }}>● Enabled</span>
              ) : (
                <span style={{ color: "#b91c1c", fontWeight: 600 }}>● Disabled</span>
              )}
            </div>

            <div style={{ color: "#475569", fontWeight: 600 }}>Offer ID</div>
            <div data-testid="effective-offer-id" style={{ fontFamily: "monospace", fontSize: "13px", color: "#0f172a" }}>
              {settings.offer_id || (
                <span style={{ color: "#64748b" }}>
                  none — add at least one pricing rule below to auto-create the default offer
                </span>
              )}
            </div>

            <div style={{ color: "#475569", fontWeight: 600 }}>Default Price</div>
            <div data-testid="effective-offer-price" style={{ fontWeight: 600, color: "#0f172a" }}>
              {settings.unlock_price ? (
                <span style={{ display: "inline-flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                  <span data-testid="default-price-value">{settings.unlock_price}</span>
                  <span
                    style={{
                      fontSize: "11px",
                      fontWeight: 500,
                      color: "#92400e",
                      background: "#fef3c7",
                      border: "1px solid #fde68a",
                      padding: "2px 8px",
                      borderRadius: "999px",
                      whiteSpace: "nowrap",
                    }}
                    title="This discount is applied to EVERY vehicle that doesn't match a more specific rule below."
                  >
                    applies to all vehicles without a matching rule
                  </span>
                  <button
                    type="button"
                    data-testid="clear-default-price-btn"
                    onClick={async () => {
                      if (!window.confirm("Clear the Default Price fallback?\n\nVehicles that don't match a specific pricing rule will no longer show a discount — they'll display 'Call for Best Price' or similar instead.")) return;
                      try {
                        const next = { ...settings, unlock_price: null };
                        await apiFetch("/unlock/settings", { method: "PUT", data: next });
                        setSettings(next);
                        toast.success("Default Price cleared. Only specific pricing rules apply now.");
                      } catch (e) {
                        toast.error("Failed to clear default price");
                      }
                    }}
                    style={{
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "#b91c1c",
                      background: "transparent",
                      border: "1px solid #fecaca",
                      padding: "3px 10px",
                      borderRadius: "6px",
                      cursor: "pointer",
                    }}
                  >
                    Clear
                  </button>
                </span>
              ) : (
                <span style={{ color: "#64748b", fontWeight: 400 }} data-testid="default-price-empty">
                  none — resolved per-vehicle from pricing rules (no fallback discount)
                </span>
              )}
            </div>

            <div style={{ color: "#475569", fontWeight: 600 }}>Button Copy</div>
            <div>{settings.button_text || "Get My Price"}</div>

            <div style={{ color: "#475569", fontWeight: 600 }}>Headline</div>
            <div>{settings.headline_text || "Unlock Your Best Price"}</div>
          </div>
          <p className="setting-description" style={{ marginTop: "10px", fontSize: "12px", color: "#64748b" }}>
            This panel is read-only. To change copy/colors edit the sections below; pricing rules live in the Flexible Pricing Rules section.
          </p>
        </div>

        {/* iter126 — Unified Pricing Rules section.
            Previously this lived as two separate cards ("Flexible Pricing Rules"
            and "Smart Pricing Rules") that wrote to the same underlying
            /unlock/pricing-rules endpoint. Dealers couldn't tell which UI applied
            to what, so we merged them: one table sorted by priority cascade, one
            inline add-form for the simple cases (Default / New-Used / Specific
            VIN), and two prominent buttons for the YMMT step-by-step builder. */}
        <div className="settings-section" data-testid="flexible-pricing-section">
          <h3>Pricing Rules</h3>
          <p className="setting-description" style={{ marginBottom: "16px" }}>
            All your unlock-price rules live here. The server evaluates them in this order — the first match wins:
          </p>
          <ol style={{ margin: "0 0 16px 22px", padding: 0, color: "#475569", fontSize: "13px", lineHeight: 1.6 }}>
            <li><strong>Specific VIN / Stock #</strong> — overrides any other rule for that exact vehicle.</li>
            <li><strong>Year / Make / Model / Trim</strong> — targets a model line (e.g. &quot;All 2024 Mustang GTs&quot;).</li>
            <li><strong>New / Used Condition</strong> — broad rule for an entire condition group.</li>
            <li><strong>Default</strong> — fallback for every vehicle that didn&apos;t match anything above.</li>
          </ol>
          
          {/* Existing Rules — unified table (basic + smart rules combined). */}
          {(pricingRules.length > 0 || smartRules.length > 0) && (
            <div style={{ marginBottom: "16px" }} className="table-scroll" data-testid="flexible-pricing-rules-scroll">
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid #e2e8f0", textAlign: "left" }}>
                    <th style={{ padding: "8px 12px" }}>Type</th>
                    <th style={{ padding: "8px 12px" }}>Match</th>
                    <th style={{ padding: "8px 12px" }}>Unlock Price</th>
                    <th style={{ padding: "8px 12px" }} className="hide-md">Priority</th>
                    <th style={{ padding: "8px 12px" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {/* Sort: VIN overrides → YMMT smart rules → condition (New/Used) → default */}
                  {[...pricingRules, ...smartRules]
                    .slice()
                    .sort((a, b) => {
                      const rank = (r) => {
                        if (r.rule_type === "default") return 4;
                        if (r.rule_type === "condition") return 3;
                        if (r.rule_type === "vin_override" && isSmartRuleIdentifier(r.vin)) return 2;
                        return 1; // specific VIN
                      };
                      return rank(a) - rank(b) || (b.priority || 0) - (a.priority || 0);
                    })
                    .map(rule => {
                      const isSmart = rule.rule_type === "vin_override" && isSmartRuleIdentifier(rule.vin);
                      const badge = rule.rule_type === "default"
                        ? { label: "Default", bg: "#fef3c7", color: "#92400e" }
                        : rule.rule_type === "condition"
                        ? { label: "New/Used", bg: "#dcfce7", color: "#166534" }
                        : isSmart
                        ? { label: "Year/Make/Model", bg: "#ede9fe", color: "#5b21b6" }
                        : { label: "Specific VIN", bg: "#dbeafe", color: "#1d4ed8" };
                      return (
                    <tr key={rule.id} style={{ borderBottom: "1px solid #e2e8f0" }} data-testid={`pricing-rule-${rule.id}`}>
                      <td style={{ padding: "8px 12px" }}>
                        <span style={{
                          display: "inline-block",
                          padding: "2px 8px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          fontWeight: "600",
                          background: badge.bg,
                          color: badge.color,
                        }}>
                          {badge.label}
                        </span>
                      </td>
                      <td style={{ padding: "8px 12px" }}>
                        {(() => {
                          if (rule.rule_type === "condition") {
                            return rule.condition === "new" ? "New Vehicles" : "Used Vehicles";
                          }
                          if (rule.rule_type === "default") return "All Vehicles";
                          // vin_override — could be a plain VIN OR an encoded YMMT (VD_SMART / VD_RULE) identifier.
                          const vin = rule.vin || "";
                          if (isSmartRuleIdentifier(vin)) {
                            const parsed = parseSmartIdentifier(vin) || {};
                            const ymmt = [parsed.year, parsed.make, parsed.model, parsed.trim].filter(Boolean).join(" ").trim();
                            return ymmt || "Year/Make/Model rule";
                          }
                          if (vin.startsWith("VD_RULE:")) {
                            const [cond, year, make, model, trim, basedOn] = vin
                              .replace("VD_RULE:", "")
                              .split("|");
                            const ymmt = [year, make, model, trim].filter(Boolean).join(" ").trim();
                            const condLabel = cond === "new" ? "New Vehicles" : cond === "used" ? "Used Vehicles" : "";
                            const basedOnLabel = basedOn === "msrp" ? "MSRP" : basedOn === "sale_price" ? "Sale Price" : "";
                            const suffix = [condLabel, basedOnLabel].filter(Boolean).join(" · ");
                            if (ymmt && suffix) return `${ymmt} (${suffix})`;
                            if (ymmt) return ymmt;
                            if (suffix) return suffix;
                            return "Year/Make/Model rule";
                          }
                          return <span style={{ fontFamily: "monospace" }}>{vin}</span>;
                        })()}
                      </td>
                      <td style={{ padding: "8px 12px", fontWeight: "600" }}>{rule.unlock_price}</td>
                      <td style={{ padding: "8px 12px", color: "#6b7280" }} className="hide-md">{rule.priority}</td>
                      <td style={{ padding: "8px 12px" }}>
                        <button
                          onClick={() => handleDeleteRule(rule.id)}
                          style={{ color: "#dc2626", background: "none", border: "none", cursor: "pointer", fontSize: "14px" }}
                          data-testid={`delete-rule-${rule.id}`}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          )}
          
          {/* Add New Rule Form */}
          <div style={{ display: "flex", gap: "8px", alignItems: "flex-end", flexWrap: "wrap", padding: "12px", background: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
            <div style={{ flex: "0 0 auto" }}>
              <label style={{ display: "block", fontSize: "12px", fontWeight: "600", marginBottom: "4px", color: "#374151" }}>Rule Type</label>
              <select
                value={newRule.rule_type}
                onChange={(e) => setNewRule(prev => ({ ...prev, rule_type: e.target.value, condition: "new", vin: "" }))}
                style={{ padding: "6px 10px", borderRadius: "6px", border: "1px solid #d1d5db", fontSize: "14px" }}
                data-testid="new-rule-type"
              >
                <option value="default">Default (All Vehicles)</option>
                <option value="condition">New / Used</option>
                <option value="vin_override">Vehicle Override (VIN/Stock#)</option>
              </select>
            </div>
            
            {newRule.rule_type === "condition" ? (
              <div style={{ flex: "0 0 auto" }}>
                <label style={{ display: "block", fontSize: "12px", fontWeight: "600", marginBottom: "4px", color: "#374151" }}>Condition</label>
                <select
                  value={newRule.condition}
                  onChange={(e) => setNewRule(prev => ({ ...prev, condition: e.target.value }))}
                  style={{ padding: "6px 10px", borderRadius: "6px", border: "1px solid #d1d5db", fontSize: "14px" }}
                  data-testid="new-rule-condition"
                >
                  <option value="new">New Vehicles</option>
                  <option value="used">Used Vehicles</option>
                </select>
              </div>
            ) : newRule.rule_type === "vin_override" ? (
              <div style={{ flex: "1 1 140px" }}>
                <label style={{ display: "block", fontSize: "12px", fontWeight: "600", marginBottom: "4px", color: "#374151" }}>VIN or Stock #</label>
                <input
                  type="text"
                  value={newRule.vin}
                  onChange={(e) => setNewRule(prev => ({ ...prev, vin: e.target.value.toUpperCase() }))}
                  placeholder="e.g., 026015 or JA4G2N5G..."
                  style={{ padding: "6px 10px", borderRadius: "6px", border: "1px solid #d1d5db", fontSize: "14px", width: "100%" }}
                  data-testid="new-rule-vin"
                />
              </div>
            ) : (
              <div style={{ flex: "0 0 auto", display: "flex", alignItems: "center", padding: "6px 12px", background: "#fef3c7", borderRadius: "6px", color: "#92400e", fontSize: "13px" }}>
                Applies to all vehicles without specific rules
              </div>
            )}
            
            <div style={{ flex: "1 1 120px" }}>
              <label style={{ display: "block", fontSize: "12px", fontWeight: "600", marginBottom: "4px", color: "#374151" }}>Unlock Price</label>
              <input
                type="text"
                value={newRule.unlock_price}
                onChange={(e) => setNewRule(prev => ({ ...prev, unlock_price: e.target.value }))}
                placeholder="e.g., $500 off"
                style={{ padding: "6px 10px", borderRadius: "6px", border: "1px solid #d1d5db", fontSize: "14px", width: "100%" }}
                data-testid="new-rule-price"
              />
            </div>
            
            <button
              onClick={handleAddRule}
              disabled={isAddingRule}
              className="btn-primary"
              style={{ padding: "6px 16px", whiteSpace: "nowrap" }}
              data-testid="add-pricing-rule-btn"
            >
              {isAddingRule ? "Adding..." : "Add Rule"}
            </button>
          </div>

          {/* iter126 — Year/Make/Model rules use a richer step-by-step
              builder (autocomplete YMMT, "based on MSRP vs Sale Price",
              etc.) that doesn't fit comfortably into the inline form
              above. We keep them out of the inline form on purpose and
              route dealers to the dedicated builder pages instead. */}
          <div style={{ marginTop: "12px", display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
            <span style={{ fontSize: "13px", color: "#475569" }}>
              Need to target a Year / Make / Model / Trim?
            </span>
            <button
              onClick={() => onNavigate && onNavigate("pricing-rules-new")}
              className="btn-secondary"
              style={{ padding: "6px 14px", fontSize: "13px" }}
              data-testid="manage-pricing-rules-new-btn"
            >
              <Plus size={14} /> New Vehicle Rules
            </button>
            <button
              onClick={() => onNavigate && onNavigate("pricing-rules-used")}
              className="btn-secondary"
              style={{ padding: "6px 14px", fontSize: "13px" }}
              data-testid="manage-pricing-rules-used-btn"
            >
              <Plus size={14} /> Used Vehicle Rules
            </button>
          </div>
        </div>

        {/* iter126 — Smart Pricing Rules section removed. Its rules now
            appear in the unified table above; the New/Used builder buttons
            are exposed inline in the add-rule area. */}

        {/* iter99 — Branding Section
            When the master admin has enabled "Dealer Unlock Control" for this
            dealer, render the full DealerBrandingTab editor so dealers get the
            same CTA Template gallery / headline scale / colors / button text
            controls the master admin uses. Otherwise show the locked notice. */}
        {settings.dealer_unlock_control_enabled ? (
          <div className="settings-section" data-testid="branding-self-service-section">
            <DealerBrandingTab
              endpointMode="dealer-self"
              dealer={{ id: user?.business_id, name: user?.business_name || "Your Dealership" }}
              unlockSettings={settings}
              onRefresh={async () => {
                try {
                  const r = await apiFetch("/unlock/settings", { method: "GET" });
                  setSettings(r.data);
                } catch (_e) { /* swallow */ }
              }}
              disabled={false}
            />
          </div>
        ) : (
        <div className="settings-section" data-testid="branding-section">
          <h3>Branding</h3>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4" data-testid="branding-locked-notice">
            <div className="flex items-start gap-2 text-blue-800">
              <Lock size={16} className="mt-0.5 flex-shrink-0" />
              <p className="text-sm text-blue-800 leading-relaxed m-0">
                Colors are managed by Verified Demand to maximize conversion. Want to use your brand colors?{" "}
                <a
                  href="mailto:support@verifieddemand.com?subject=Custom%20brand%20colors%20request"
                  className="font-semibold underline hover:text-blue-900"
                  data-testid="branding-contact-am-link"
                >
                  Contact your account manager
                </a>
                .
              </p>
            </div>
          </div>

          <div className="color-grid" data-testid="locked-color-grid">
            <LockedColorField
              label="Primary Color"
              value={settings.primary_color || "#2563eb"}
              testId="locked-color-primary"
            />
            <LockedColorField
              label="Button Color"
              value={settings.button_color || "#2563eb"}
              testId="locked-color-button"
            />
            <LockedColorField
              label="Button Text Color"
              value={settings.text_color || "#ffffff"}
              testId="locked-color-button-text"
            />
            <LockedColorField
              label="Modal Background"
              value={settings.modal_background || "#ffffff"}
              testId="locked-color-modal-bg"
            />
          </div>
          
          <div className="setting-row">
            <label>Logo URL (optional)</label>
            <input
              type="text"
              value={settings.logo_url || ""}
              onChange={(e) => handleChange("logo_url", e.target.value)}
              placeholder="https://yourdealership.com/logo.png"
            />
          </div>
        </div>
        )}

        {/* Behavior Section */}
        <div className="settings-section">
          <h3>Behavior</h3>
          
          <div className="setting-row">
            <label>Unlock Persistence</label>
            <select
              data-testid="unlock-persist-days-select"
              value={settings.unlock_persist_days ?? 30}
              onChange={(e) => handleChange("unlock_persist_days", parseInt(e.target.value))}
            >
              <option value={0}>2 minutes (Demo)</option>
              <option value={1}>24 hours</option>
              <option value={3}>72 hours</option>
              <option value={7}>7 days</option>
              <option value={14}>14 days</option>
              <option value={21}>21 days</option>
              <option value={30}>30 days</option>
            </select>
            <p className="setting-description">
              How long the unlock remains valid for a visitor before they need to re-verify. Default is 30 days (dealer-scoped — unlocks all vehicles at this dealer). <strong>Demo (2 min)</strong> is for testing the lock/unlock cycle — visitors will need to re-verify every two minutes.
            </p>
          </div>
          
          <div className="setting-row">
            <label className="toggle-label">
              <span>Require SMS Verification</span>
              <input
                type="checkbox"
                checked={settings.require_sms_verification}
                onChange={(e) => handleChange("require_sms_verification", e.target.checked)}
              />
              <span className="toggle-switch"></span>
            </label>
            <p className="setting-description">
              Strongly recommended. Verifies mobile number before revealing price.
            </p>
          </div>
          
          <div className="setting-row">
            <label className="toggle-label">
              <span>Require Email</span>
              <input
                type="checkbox"
                checked={settings.require_email}
                onChange={(e) => handleChange("require_email", e.target.checked)}
              />
              <span className="toggle-switch"></span>
            </label>
          </div>
          
          <div className="setting-row">
            <label className="toggle-label">
              <span>Require Last Name</span>
              <input
                type="checkbox"
                checked={settings.require_last_name}
                onChange={(e) => handleChange("require_last_name", e.target.checked)}
              />
              <span className="toggle-switch"></span>
            </label>
          </div>
        </div>

        {/* Unlock Notifications Section */}
        <div className="settings-section" data-testid="unlock-notify-section">
          <h3>Notify on Unlock</h3>
          <div className="setting-row">
            <label htmlFor="unlock-notify-emails">Notification Recipients</label>
            <textarea
              id="unlock-notify-emails"
              data-testid="unlock-notify-emails-input"
              rows={3}
              placeholder="sales@yourdealership.com, gm@yourdealership.com"
              value={
                Array.isArray(settings.unlock_notify_emails)
                  ? settings.unlock_notify_emails.join(", ")
                  : (settings.unlock_notify_emails || "")
              }
              onChange={(e) => handleChange("unlock_notify_emails", e.target.value)}
              style={{ fontFamily: "monospace", fontSize: 13, lineHeight: 1.5 }}
            />
            <p className="setting-description">
              Send a notification email to these recipients every time a visitor unlocks a price.
              Separate multiple emails with commas, spaces, or new lines. Leave blank to disable.
            </p>
          </div>
        </div>

        {/* iter132 — Call Dealer for Special Price panel customization.
            This panel renders on the unlocked state for vehicles that
            don't match any pricing rule (after iter131 removed the
            global settings.unlock_price fallback). Both fields support
            a {{phone}} merge tag that resolves to the dealer's primary
            phone. Headline auto-becomes a tel: link when phone is set. */}
        <div className="settings-section" data-testid="call-dealer-panel-section">
          <h3>&ldquo;Call Dealer&rdquo; Panel (for vehicles with no rule)</h3>
          <p className="setting-description" style={{ marginTop: "-6px", marginBottom: "16px" }}>
            When a visitor unlocks a vehicle that doesn&apos;t match any of your pricing rules, the embed shows this panel instead of a discount number. Use <code>&#123;&#123;phone&#125;&#125;</code> to insert the dealership phone number (auto-becomes a tap-to-call link on mobile).
          </p>

          <div className="setting-row">
            <label>Headline</label>
            <input
              type="text"
              data-testid="call-dealer-headline-input"
              value={settings.call_dealer_headline ?? ""}
              maxLength={120}
              onChange={(e) => handleChange("call_dealer_headline", e.target.value)}
              placeholder="Call Dealer for Special Price"
            />
            <p className="setting-description">
              Max 120 characters. Default: <em>&ldquo;Call Dealer for Special Price&rdquo;</em>.
            </p>
          </div>

          <div className="setting-row">
            <label>Subcopy</label>
            <input
              type="text"
              data-testid="call-dealer-subcopy-input"
              value={settings.call_dealer_subcopy ?? ""}
              maxLength={160}
              onChange={(e) => handleChange("call_dealer_subcopy", e.target.value)}
              placeholder="on this vehicle"
            />
            <p className="setting-description">
              Max 160 characters. Default: <em>&ldquo;on this vehicle&rdquo;</em>. Examples: <code>Call &#123;&#123;phone&#125;&#125; for our best offer</code> or <code>We&apos;ll match any verified offer</code>.
            </p>
          </div>

          {/* Live preview so dealers see exactly what visitors will see */}
          <div
            data-testid="call-dealer-preview"
            style={{
              marginTop: "12px",
              background: "linear-gradient(135deg,#f8fafc 0%,#eef2f7 100%)",
              border: "1px solid #e2e8f0",
              borderRadius: 14,
              padding: "18px 20px",
              display: "flex",
              alignItems: "center",
              gap: 14,
              boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
              fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif",
            }}
          >
            <div
              style={{
                color: "#0f172a",
                background: "#fff",
                border: "1px solid #e2e8f0",
                borderRadius: 12,
                width: 42,
                height: 42,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a", letterSpacing: "-0.01em", lineHeight: 1.25 }}>
                {(settings.call_dealer_headline || "Call Dealer for Special Price").replace(/\{\{\s*phone\s*\}\}/gi, settings.dealer_phone || "[your phone]")}
              </div>
              <div style={{ fontSize: 12.5, color: "#475569", fontWeight: 500, marginTop: 3, lineHeight: 1.35 }}>
                {(settings.call_dealer_subcopy || "on this vehicle").replace(/\{\{\s*phone\s*\}\}/gi, settings.dealer_phone || "[your phone]")}
              </div>
            </div>
          </div>
          <p className="setting-description" style={{ marginTop: 8, fontSize: 12, color: "#64748b" }}>
            ↑ Live preview. {settings.dealer_phone ? `Phone "${settings.dealer_phone}" will resolve {{phone}} tags.` : "No dealer phone set yet — {{phone}} tags will appear as \"[your phone]\" in the preview."}
          </p>
        </div>

        {/* CRM Integration Section */}
        <div className="settings-section">
          <h3>CRM Integration</h3>

          <div className="setting-row">
            <label>CRM Name</label>
            <input
              type="text"
              data-testid="crm-name-input"
              value={settings.crm_name || ""}
              onChange={(e) => handleChange("crm_name", e.target.value)}
              placeholder="e.g. HubSpot, VinSolutions, DealerSocket"
            />
            <p className="setting-description">
              The name of this dealer&apos;s CRM. Used internally so our team knows which system this dealer pushes leads to.
            </p>
          </div>

          <div className="setting-row">
            <label className="toggle-label">
              <span>Enable Webhook</span>
              <input
                type="checkbox"
                checked={settings.webhook_enabled}
                onChange={(e) => handleChange("webhook_enabled", e.target.checked)}
              />
              <span className="toggle-switch"></span>
            </label>
            <p className="setting-description">
              Send lead data to your CRM when a visitor unlocks a price.
            </p>
          </div>
          
          {settings.webhook_enabled && (
            <>
              <div className="setting-row">
                <label>Webhook URL</label>
                <input
                  type="url"
                  value={settings.webhook_url || ""}
                  onChange={(e) => handleChange("webhook_url", e.target.value)}
                  placeholder="https://your-crm.com/webhook/leads"
                />
              </div>
              
              <div className="setting-row">
                <label>Webhook Secret (for HMAC signature)</label>
                <input
                  type="text"
                  value={settings.webhook_secret || ""}
                  onChange={(e) => handleChange("webhook_secret", e.target.value)}
                  placeholder="Auto-generated if empty"
                  className="mono"
                />
                <p className="setting-description">
                  We sign each webhook payload with this secret using HMAC-SHA256. 
                  Verify the X-VD-Signature header to ensure authenticity.
                </p>
              </div>
            </>
          )}

          <div className="setting-row" style={{ borderTop: "1px solid #E5E7EB", paddingTop: 16, marginTop: 8, display: "flex", justifyContent: "flex-end" }}>
            <button
              className="btn-primary"
              onClick={handleSave}
              disabled={isSaving}
              data-testid="save-unlock-settings-bottom"
            >
              <Save size={14} /> {isSaving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>

        {/* iter100 — "CTA Live Preview" section removed per user request.
            Was duplicative of the preview inside DealerBrandingTab and
            didn't reflect real on-site rendering. */}
      </div>{/* end .settings-grid */}

      {/* iter100 — preview sidebar removed per user request:
          duplicated the existing CTA preview inside DealerBrandingTab and
          confused dealers (and was visually clipping on widescreen). The
          source of truth for preview is now the dealer's test site. */}
    </div>{/* end .unlock-settings-layout */}

      <style>{`
        .unlock-settings-layout {
          display: flex;
          gap: 24px;
          align-items: flex-start;
          /* iter96 — Issue 8: keep the right-side preview from kissing the
             viewport edge on widescreen monitors. */
          padding-right: 8px;
          max-width: 100%;
          overflow-x: clip; /* allows children to wrap without horizontal scroll */
        }
        .unlock-modal-preview {
          width: 300px;
          flex-shrink: 0;
          position: sticky;
          top: 24px;
          /* iter96 — Issue 8: ensure preview content wraps cleanly */
          word-wrap: break-word;
          overflow-wrap: anywhere;
        }
        .ump-modal-inner * { box-sizing: border-box; }
        .ump-disclosure { font-size: 9px; color: #9CA3AF; margin-top: 6px; line-height: 1.4; word-wrap: break-word; overflow-wrap: anywhere; }

        @media (max-width: 1100px) {
          /* On narrower viewports the preview drops below the form so
             nothing gets cropped on the right. */
          .unlock-settings-layout { flex-direction: column; }
          .unlock-modal-preview { width: 100%; max-width: 360px; position: static; }
        }
        .ump-header {
          display: flex;
          flex-direction: column;
          gap: 2px;
          padding: 12px 14px;
          background: white;
          border: 1px solid #E5E7EB;
          border-radius: 12px 12px 0 0;
          border-bottom: none;
        }
        .ump-phone {
          background: white;
          border: 1px solid #E5E7EB;
          border-radius: 0 0 12px 12px;
          padding: 12px;
        }
        .ump-modal { border-radius: 10px; overflow: hidden; border: 1px solid #E5E7EB; box-shadow: 0 4px 20px rgba(0,0,0,0.08); }
        .ump-modal-inner { display: flex; flex-direction: column; }
        .ump-vehicle-card { position: relative; }
        .ump-vehicle-img { height: 80px; background: linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%); }
        .ump-vehicle-info { padding: 8px 10px 4px; display: flex; flex-direction: column; gap: 2px; }
        .ump-vehicle-title { font-size: 11px; font-weight: 600; color: #111827; }
        .ump-vehicle-price { font-size: 11px; font-weight: 700; }
        .ump-cta-overlay { padding: 6px 10px 10px; display: flex; flex-direction: column; align-items: center; gap: 6px; border-top: 1px solid #F3F4F6; }
        .ump-cta-headline { font-size: 16px; text-align: center; }
        .ump-cta-btn { padding: 6px 14px; border: none; border-radius: 6px; font-size: 11px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 3px; }
        .ump-form-area { padding: 12px; }
        .ump-form-header { padding: 0 0 8px; }
        .ump-headline { font-size: 13px; font-weight: 700; margin: 0 0 4px; }
        .ump-subtext { font-size: 10px; color: #6B7280; margin: 0; }
        .ump-form-fields { display: flex; flex-direction: column; gap: 5px; margin-bottom: 8px; }
        .ump-mock-input { background: #F9FAFB; border: 1px solid #E5E7EB; border-radius: 5px; padding: 6px 8px; font-size: 10px; color: #9CA3AF; }
        .ump-submit-btn { width: 100%; padding: 7px; border: none; border-radius: 6px; font-size: 11px; font-weight: 600; cursor: pointer; }

        .settings-grid {
          display: grid;
          gap: 20px;
        }
        
        .settings-section {
          background: white;
          border-radius: 12px;
          padding: 22px;
          border: 1px solid #E5E7EB;
        }
        
        .settings-section h3 {
          margin: 0 0 18px 0;
          font-size: 14px;
          font-weight: 600;
          color: #111827;
          padding-bottom: 12px;
          border-bottom: 1px solid #F3F4F6;
        }
        
        .setting-row {
          margin-bottom: 18px;
        }
        
        .setting-row:last-child {
          margin-bottom: 0;
        }
        
        .setting-row > label:not(.toggle-label) {
          display: block;
          font-size: 13px;
          font-weight: 500;
          color: #374151;
          margin-bottom: 7px;
        }
        
        .setting-row input[type="text"],
        .setting-row input[type="url"],
        .setting-row input[type="number"],
        .setting-row textarea {
          width: 100%;
          padding: 9px 13px;
          border: 1.5px solid #E5E7EB;
          border-radius: 8px;
          font-size: 14px;
          transition: border-color 0.15s;
        }
        
        .setting-row input:focus,
        .setting-row textarea:focus {
          outline: none;
          border-color: #005CFF;
          box-shadow: 0 0 0 3px rgba(107, 47, 217, 0.08);
        }
        
        .setting-row textarea {
          resize: vertical;
          min-height: 60px;
        }
        
        .setting-row input.mono {
          font-family: monospace;
          font-size: 13px;
        }
        
        .setting-description {
          font-size: 12px;
          color: #9CA3AF;
          margin-top: 5px;
          margin-bottom: 0;
        }
        
        .headline-slider {
          width: 100%;
          height: 8px;
          background: #e5e7eb;
          border-radius: 4px;
          cursor: pointer;
          -webkit-appearance: none;
          appearance: none;
        }
        
        .headline-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 20px;
          height: 20px;
          background: #005CFF;
          border-radius: 50%;
          cursor: pointer;
          box-shadow: 0 1px 3px rgba(0,0,0,0.2);
        }
        
        .headline-slider::-moz-range-thumb {
          width: 20px;
          height: 20px;
          background: #005CFF;
          border-radius: 50%;
          cursor: pointer;
          border: none;
        }
        
        .slider-labels {
          display: flex;
          justify-content: space-between;
          font-size: 11px;
          color: #9ca3af;
          margin-top: 4px;
        }
        
        .toggle-label {
          display: flex;
          align-items: center;
          gap: 12px;
          cursor: pointer;
        }
        
        .toggle-label span:first-child {
          font-size: 14px;
          font-weight: 500;
          color: #374151;
        }
        
        .toggle-label input[type="checkbox"] {
          display: none;
        }
        
        .toggle-switch {
          position: relative;
          width: 48px;
          height: 26px;
          background: #d1d5db;
          border-radius: 13px;
          transition: background-color 0.2s;
        }
        
        .toggle-switch::after {
          content: "";
          position: absolute;
          top: 3px;
          left: 3px;
          width: 20px;
          height: 20px;
          background: white;
          border-radius: 50%;
          transition: transform 0.2s;
          box-shadow: 0 1px 3px rgba(0,0,0,0.2);
        }
        
        .toggle-label input:checked + .toggle-switch {
          background: #2563eb;
        }
        
        .toggle-label input:checked + .toggle-switch::after {
          transform: translateX(22px);
        }
        
        .color-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 16px;
          margin-bottom: 20px;
        }
        
        .color-input-wrapper {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .color-input-wrapper input[type="color"] {
          width: 40px;
          height: 40px;
          padding: 0;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          cursor: pointer;
        }
        
        .color-input-wrapper .color-text {
          flex: 1;
          font-family: monospace;
        }

        /* iter100 — removed unused .preview-section / .preview-container /
           .cta-preview / .cta-callout-svg / .preview-unlock-btn styles —
           the "CTA Live Preview" + right-rail modal preview were both
           deleted per user request. */
        
        .headline-select {
          width: 100%;
          padding: 10px 14px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 14px;
          background: white;
          cursor: pointer;
        }
        
        .headline-select:focus {
          outline: none;
          border-color: #2563eb;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
        }
      `}</style>
    </div>
  );
};

// iter94 — Issue 18: Intentionally-readonly color field with lock icon.
// Looks deliberate (cursor-default, low-contrast pattern, lock chip) rather than broken.
const LockedColorField = ({ label, value, testId }) => (
  <div className="setting-row locked-color-row" data-testid={testId}>
    <label
      className="flex items-center gap-1.5"
      style={{ color: "#374151", fontWeight: 500 }}
    >
      <Lock size={12} className="text-gray-400" />
      {label}
    </label>
    <div
      className="locked-color-display"
      style={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
        padding: "8px 12px",
        border: "1px solid #E5E7EB",
        borderRadius: "8px",
        background: "#FAFAFA",
        cursor: "default",
        userSelect: "none",
      }}
    >
      <span
        aria-hidden
        style={{
          width: "32px",
          height: "32px",
          borderRadius: "6px",
          background: value,
          border: "1px solid #D1D5DB",
          flexShrink: 0,
          boxShadow: "inset 0 0 0 2px #FFFFFF",
        }}
      />
      <span
        style={{
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          fontSize: "13px",
          color: "#4B5563",
          letterSpacing: "0.02em",
        }}
      >
        {value.toUpperCase()}
      </span>
      <span
        style={{
          marginLeft: "auto",
          fontSize: "10px",
          fontWeight: 700,
          letterSpacing: "0.08em",
          color: "#6B7280",
          padding: "3px 8px",
          background: "#F3F4F6",
          border: "1px solid #E5E7EB",
          borderRadius: "999px",
          textTransform: "uppercase",
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
        }}
      >
        <Lock size={9} /> Managed
      </span>
    </div>
  </div>
);

export default UnlockSettingsPage;
